'use client';

import './styles/default.scss';
import { classes } from '@/utils/classes/classes';
import { useStore } from '@/store/store';
import { useShallow } from 'zustand/react/shallow';
import { ValueTicker } from '@/app/elements/value-ticker/value-ticker';
import { getText } from '@/utils/text/text';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { useGlobalContext } from '@/store/contexts/global-context';

const JACKPOT_TICKER_OBSERVER_OPTIONS = { rootMargin: '100px', threshold: 0 };

type SwjJackpotTickerProps = {
    isSmall?: boolean;
    observeVisibility?: boolean;
};

export const isSwjJackpotTickerEnabled = (variant?: string) => variant === 'control';

export const SwjJackpotTicker: React.FC<SwjJackpotTickerProps> = ({ isSmall, observeVisibility = true }) => {
    const { swjTickerRedesignVariant } = useGlobalContext();
    const showTickers = isSwjJackpotTickerEnabled(swjTickerRedesignVariant);

    const [tickerRef, isInView] = useIntersectionObserver<HTMLDivElement>(JACKPOT_TICKER_OBSERVER_OPTIONS);
    const { activeIndex, loading, updatedTiers } = useStore(
        useShallow((state) => ({
            activeIndex: state.siteWideJackpot.activeIndex,
            loading: state.siteWideJackpot.loading,
            updatedTiers: state.siteWideJackpot.updatedTiers
        }))
    );

    if (!showTickers) {
        return null;
    }

    const prevActiveIndex = activeIndex === 0 ? updatedTiers.length - 1 : activeIndex - 1;
    const animateValues = !observeVisibility || isInView;
    const isLoading = loading || updatedTiers.length === 0;

    return (
        <div
            ref={observeVisibility ? tickerRef : undefined}
            className={classes('game-tiles-container', isSmall && 'small')}>
            {isLoading ? (
                <div className="loading-state">
                    <span>{getText({ text: 'components.siteWideJackpots.loadingStatus' })}</span>
                </div>
            ) : (
                updatedTiers.map((tier, index) => (
                    <div
                        key={tier.tier_id}
                        className={classes(
                            'game-tile_image--ticker',
                            index === activeIndex && 'active',
                            index === prevActiveIndex && 'previous'
                        )}>
                        <img
                            className="game-tile_image--ticker__jackpot-img"
                            src={tier.logo1.url}
                            alt={`${tier.tier_name?.[0]?.text} logo`}
                        />
                        <div className="game-tile_image--ticker__jackpot-info">
                            <p>{tier.tier_name?.[0]?.text}</p>
                            <div className="game-tile_image--ticker__card-value">
                                {animateValues && (
                                    <ValueTicker
                                        amount={Number(tier.currentAmount)}
                                        prevAmount={Number(tier.prevAmount)}
                                        incrementTime={1000} // Increment every second
                                        periodTime={5000} // For 5 seconds
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};
